import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import Modal from "../../../../../../components/base/Modal";
import type { ThemeClasses } from "../../../../../../types/components";
import type { Status } from "../../../../../../store/admin/statusesStore";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { getCustomSelectStyles } from "../../../../shared/theme/selectStyles";

interface AvailableUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  username?: string;
}

interface DriverFormState {
  userId: string;
  firstName: string;
  lastName: string;
  licenseId: string;
  licenseExp: string;
  licenseExpedition: string;
  status_id: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  profileFile: string;
  companyId: string;
  groupId: string;
}

interface AddDriverModalProps {
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  driverForm: DriverFormState;
  availableUsers: AvailableUser[];
  imagePreview: string | null;
  isSubmitting: boolean;
  statuses: Status[];
  statusesLoading?: boolean;
  onInputChange: (field: string, value: string) => void;
  onUserSelect: (userId: string) => void;
  onImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onClose: () => void;
  onSubmit: () => void;
}

export default function AddDriverModal({
  isDarkMode,
  themeClasses,
  driverForm,
  availableUsers,
  imagePreview,
  isSubmitting,
  statuses,
  statusesLoading,
  onInputChange,
  onUserSelect,
  onImageChange,
  onClose,
  onSubmit,
}: AddDriverModalProps) {
  const { t } = useTranslation("adminSections");
  const statusOptions = getStatusOptionsForEntity(statuses, "drivers", t);

  return (
    <Modal
      title={t("drivers.modal.addTitle")}
      themeClasses={themeClasses}
      onClose={onClose}
      content={
        <div className="space-y-4 w-[500px] max-w-full">
          <div className="col-span-2 flex justify-center mb-4">
            <div className="relative w-48 h-48">
              {imagePreview ? (
                <img
                  src={imagePreview || ""}
                  // alt="Driver Preview"
                  className={`w-full h-full object-cover rounded-full border-2 ${themeClasses.border}`}
                />
                ) : (
                  <div className="flex w-full h-full object-cover bg-gray-200 justify-center items-center rounded-full">
                    <i className="ri-camera-line text-[3rem] text-gray-400"></i>
                  </div>
                )
              }
              <label
                htmlFor="add-driver-image"
                className="absolute bottom-0 right-0 w-10 h-10 text-center bg-primary-500 p-2 rounded-full cursor-pointer"
              >
                <i className="ri-camera-line"></i>
                <input
                  id="add-driver-image"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onImageChange}
                />
              </label>
            </div>
          </div>

          <div>
            <label
              className={`block text-sm font-medium ${themeClasses.text} mb-1`}
            >
              {t("drivers.fields.user")} <span className="text-red-500">*</span>
            </label>
            <Select
              value={availableUsers
                .map((user) => ({
                  value: user.id,
                  label: user.username ? `${user.username} (${user.email})` : `${user.firstName} ${user.lastName} (${user.email})`,
                  id: user.id,
                }))
                .find((option) => option.id === driverForm.userId) || null}
              onChange={(selectedOption: any) => onUserSelect(selectedOption?.id || "")}
              options={availableUsers.map((user) => ({
                value: user.id,
                label: user.username ? `${user.username} (${user.email})` : `${user.firstName} ${user.lastName} (${user.email})`,
                id: user.id,
              }))}
              placeholder={t("drivers.fields.selectUser")}
              isSearchable
              isClearable
              styles={getCustomSelectStyles(isDarkMode)}
              menuPortalTarget={document.body}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                className={`block text-sm font-medium ${themeClasses.text} mb-1`}
              >
                {t("drivers.fields.name")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={driverForm.firstName}
                onChange={(e) => onInputChange("firstName", e.target.value)}
                className={`w-full px-3 py-2 ${
                  isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } border ${themeClasses.cardBorder} rounded-lg ${
                  themeClasses.text
                } focus:outline-none focus:ring-2 focus:ring-primary-500`}
                placeholder={t("drivers.form.namePlaceholder")}
              />
            </div>
            <div>
              <label
                className={`block text-sm font-medium ${themeClasses.text} mb-1`}
              >
                {t("drivers.fields.lastName")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={driverForm.lastName}
                onChange={(e) => onInputChange("lastName", e.target.value)}
                className={`w-full px-3 py-2 ${
                  isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } border ${themeClasses.cardBorder} rounded-lg ${
                  themeClasses.text
                } focus:outline-none focus:ring-2 focus:ring-primary-500`}
                placeholder={t("drivers.form.lastNamePlaceholder")}
              />
            </div>
          </div>

          <div>
            <label
              className={`block text-sm font-medium ${themeClasses.text} mb-1`}
            >
              {t("drivers.fields.licenseId")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={driverForm.licenseId}
              onChange={(e) => onInputChange("licenseId", e.target.value)}
              className={`w-full px-3 py-2 ${
                isDarkMode ? "bg-dark-card" : "bg-gray-50"
              } border ${themeClasses.cardBorder} rounded-lg ${
                themeClasses.text
              } focus:outline-none focus:ring-2 focus:ring-primary-500`}
              placeholder={t("drivers.form.licensePlaceholder")}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                className={`block text-sm font-medium ${themeClasses.text} mb-1`}
              >
                {t("drivers.fields.licenseExpedition")} <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={driverForm.licenseExpedition}
                onChange={(e) => onInputChange("licenseExpedition", e.target.value)}
                className={`w-full px-3 py-2 ${
                  isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } border ${themeClasses.cardBorder} rounded-lg ${
                  themeClasses.text
                } focus:outline-none focus:ring-2 focus:ring-primary-500`}
              />
            </div>
            <div>
              <label
                className={`block text-sm font-medium ${themeClasses.text} mb-1`}
              >
                {t("drivers.fields.licenseExp")} <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={driverForm.licenseExp}
                onChange={(e) => onInputChange("licenseExp", e.target.value)}
                className={`w-full px-3 py-2 ${
                  isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } border ${themeClasses.cardBorder} rounded-lg ${
                  themeClasses.text
                } focus:outline-none focus:ring-2 focus:ring-primary-500`}
              />
            </div>
          </div>

          <div>
            <label
              className={`block text-sm font-medium ${themeClasses.text} mb-1`}
            >
              {t("drivers.fields.driverStatus")} <span className="text-red-500">*</span>
            </label>
            <Select
              options={statusOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder={
                statusesLoading
                  ? t("users.form.loadingStatuses", "Cargando...")
                  : t("users.form.selectStatus", "Selecciona un estado")
              }
              value={
                statusOptions.find(
                  (opt) => opt.value === driverForm.status_id,
                ) || null
              }
              onChange={(option: any) =>
                onInputChange("status_id", option?.value || "")
              }
              isDisabled={statusesLoading}
              isLoading={statusesLoading}
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <h4
              className={`text-sm font-semibold ${themeClasses.text} mb-3`}
            >
              {t("drivers.fields.emergencyContact")}
            </h4>
            <div className="space-y-3">
              <div>
                <label
                  className={`block text-xs font-medium ${themeClasses.textSecondary} mb-1`}
                >
                  {t("drivers.fields.contactName")}
                </label>
                <input
                  type="text"
                  value={driverForm.contactName}
                  onChange={(e) => onInputChange("contactName", e.target.value)}
                  className={`w-full px-3 py-2 ${
                    isDarkMode ? "bg-dark-card" : "bg-gray-50"
                  } border ${themeClasses.cardBorder} rounded-lg ${
                    themeClasses.text
                  } text-sm focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  placeholder={t("drivers.form.contactNamePlaceholder")}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-xs font-medium ${themeClasses.textSecondary} mb-1`}
                  >
                    {t("drivers.fields.contactPhone")}
                  </label>
                  <input
                    type="tel"
                    value={driverForm.contactPhone}
                    onChange={(e) => onInputChange("contactPhone", e.target.value)}
                    className={`w-full px-3 py-2 ${
                      isDarkMode ? "bg-dark-card" : "bg-gray-50"
                    } border ${themeClasses.cardBorder} rounded-lg ${
                      themeClasses.text
                    } text-sm focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    placeholder={t("drivers.form.phonePlaceholder")}
                  />
                </div>
                <div>
                  <label
                    className={`block text-xs font-medium ${themeClasses.textSecondary} mb-1`}
                  >
                    {t("drivers.fields.contactEmail")}
                  </label>
                  <input
                    type="email"
                    value={driverForm.contactEmail}
                    onChange={(e) => onInputChange("contactEmail", e.target.value)}
                    className={`w-full px-3 py-2 ${
                      isDarkMode ? "bg-dark-card" : "bg-gray-50"
                    } border ${themeClasses.cardBorder} rounded-lg ${
                      themeClasses.text
                    } text-sm focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    placeholder={t("drivers.form.emailPlaceholder")}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 py-2 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
            >
              {t("drivers.form.cancel")}
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={isSubmitting}
              className="flex-1 py-2 px-4 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {isSubmitting ? t("drivers.form.submitting") : t("drivers.form.save")}
            </button>
          </div>
        </div>
      }
    />
  );
}

